import { isObject } from "@okta/okta-auth-js";
import type { MaybeRef } from "@vueuse/core";
import type {
  Performance_Evaluations_Order_By,
  Scenarios_Order_By,
  Sizings_Order_By
} from "gql/api";
import { Order_By } from "gql/api";
import type { Ref } from "vue";
import { computed, ref, unref } from "vue";
import { useLogger } from "@/composables/logger";
const { info } = useLogger();

export interface SortSearchParams {
  sortField: Ref<unknown[] | undefined>;
  itemsPageNum: Ref<number>;
}

export interface SortEventArgs {
  sortField: string;
  sortOrder: -1 | 1;
}

export type SortHandler = (
  params: SortSearchParams
) => (event: SortEventArgs) => void;

export const sortHandler: SortHandler =
  ({ itemsPageNum, sortField }) =>
  (event): void => {
    info(event);
    itemsPageNum.value = 0;
    const direction = event.sortOrder > 0 ? "asc" : "desc";
    const fields = event.sortField.split(".").reverse();

    // "winning_revision.customer.customer_name"
    // becomes {winning_revision: {customer: {customer_name: "asc"}}}
    sortField.value = [
      fields.reduce((orderByObj, field, i) => {
        return i === 0 ? { [field]: direction } : { [field]: orderByObj };
      }, {})
    ];
  };

export interface SortMeta {
  field: string;
  order: -1 | 1;
}

type SortTable_Order_By =
  | Sizings_Order_By
  | Scenarios_Order_By
  | Performance_Evaluations_Order_By;

const getPrimevueSortOrder = (value: Order_By) => {
  switch (value) {
    case Order_By.Asc:
      return 1;
    case Order_By.AscNullsFirst:
      return 1;
    case Order_By.AscNullsLast:
      return 1;
    case Order_By.Desc:
      return -1;
    case Order_By.DescNullsFirst:
      return -1;
    case Order_By.DescNullsLast:
      return -1;
    default:
      return 1;
  }
};

const getUrqlOrderBy = (value: -1 | 1) => {
  switch (value) {
    case -1:
      return Order_By.Desc;
    case 1:
      return Order_By.Asc;
    default:
      return Order_By.Asc;
  }
};

export const getPrimevueSortMeta = (orderBy: SortTable_Order_By): SortMeta => {
  return Object.entries(orderBy).reduce<SortMeta>(
    (sortMeta, [key, value]) => {
      if (!isObject(value)) {
        sortMeta.order = getPrimevueSortOrder(value);
      }

      const sortFieldArray = sortMeta.field.length
        ? sortMeta.field.split(".")
        : [];

      sortFieldArray.push(key);
      sortMeta.field = sortFieldArray.join(".");
      return sortMeta;
    },
    {
      field: "",
      order: 1
    }
  );
};

export const getUrqlOrderByMeta = (multiSortMeta: SortMeta[]) => {
  return multiSortMeta.map(sortMeta => {
    const sortOrder = getUrqlOrderBy(sortMeta.order);
    const sortFieldArray = sortMeta.field.length
      ? sortMeta.field.split(".")
      : [];

    return sortFieldArray.reduceRight<SortTable_Order_By>(
      (orderBy, field, index) => {
        return index === sortFieldArray.length - 1
          ? { [field]: sortOrder }
          : { [field]: orderBy };
      },
      {}
    );
  });
};

export const usePrimeVueMultisort = (
  _orderBy: MaybeRef<SortTable_Order_By[]>
) => {
  const multiSortMeta = ref<SortMeta[]>(
    unref(_orderBy).map(getPrimevueSortMeta)
  );

  const orderBy = computed(() => getUrqlOrderByMeta(multiSortMeta.value));

  return {
    orderBy,
    multiSortMeta
  };
};
